import { useState, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Plus, Folder, Loader2, Users, Camera, Image as ImageIcon, Lock, Globe, ChevronDown } from 'lucide-react';
import { DraggableBottomSheet } from '@/components/ui/DraggableBottomSheet';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useCollections } from '@/hooks/useCollections';
import { supabase } from '@/integrations/supabase/client';
import { ImageCropEditor } from '@/components/ui/ImageCropEditor';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

interface CreateCollectionSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Item IDs to add immediately after collection creation */
  initialItemIds?: string[];
  /** Callback when collection is created and items are added */
  onCreated?: (collectionId: string) => void;
  /** Callback when entering/exiting crop mode (parent should hide itself) */
  onCropModeChange?: (isCropping: boolean) => void;
}

const colorOptions = [
  { name: 'primary', bgClass: 'bg-primary', textClass: 'text-primary' },
  { name: 'yellow', bgClass: 'bg-yellow-500', textClass: 'text-yellow-500' },
  { name: 'green', bgClass: 'bg-green-500', textClass: 'text-green-500' },
  { name: 'blue', bgClass: 'bg-blue-500', textClass: 'text-blue-500' },
  { name: 'purple', bgClass: 'bg-purple-500', textClass: 'text-purple-500' },
  { name: 'pink', bgClass: 'bg-pink-500', textClass: 'text-pink-500' },
  { name: 'red', bgClass: 'bg-red-500', textClass: 'text-red-500' },
];

export function CreateCollectionSheet({ open, onOpenChange, initialItemIds, onCreated, onCropModeChange }: CreateCollectionSheetProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedColor, setSelectedColor] = useState('primary');
  const [isCollaborative, setIsCollaborative] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [coverImageUrl, setCoverImageUrl] = useState<string | null>(null);
  const [coverImageBlob, setCoverImageBlob] = useState<Blob | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [moreOptionsOpen, setMoreOptionsOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const { collections, createCollection, addItemToCollection } = useCollections();

  // No limits during early access
  const canCreateCollection = true;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Please upload an image smaller than 10MB');
      return;
    }

    setCropFile(file);
    onCropModeChange?.(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCropSave = async (croppedBlob: Blob) => {
    // Store blob for upload during collection creation
    setCoverImageBlob(croppedBlob);
    // Create preview URL
    const previewUrl = URL.createObjectURL(croppedBlob);
    setCoverImageUrl(previewUrl);
    setCropFile(null);
    onCropModeChange?.(false);
  };

  const handleCropCancel = () => {
    setCropFile(null);
    onCropModeChange?.(false);
  };

  const handleRemoveCover = () => {
    if (coverImageUrl) {
      URL.revokeObjectURL(coverImageUrl);
    }
    setCoverImageUrl(null);
    setCoverImageBlob(null);
  };

  const handleCreate = async () => {
    if (!name.trim()) return;

    setIsCreating(true);
    try {
      // First create the collection to get its ID
      const newCollection = await createCollection.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        color: selectedColor,
        is_collaborative: isCollaborative,
        is_public: isPublic,
      });

      // If we have a cover image, upload it now
      if (coverImageBlob && newCollection) {
        const fileName = `${newCollection.id}-cover-${Date.now()}.jpg`;
        const filePath = `collection-covers/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('saved-images')
          .upload(filePath, coverImageBlob, { upsert: true });

        if (!uploadError) {
          const { data: { publicUrl } } = supabase.storage
            .from('saved-images')
            .getPublicUrl(filePath);

          // Update collection with cover image URL
          await supabase
            .from('collections')
            .update({ cover_image_url: publicUrl })
            .eq('id', newCollection.id);
        }
      }

      // Add initial items if provided (from multi-select flow)
      if (initialItemIds && initialItemIds.length > 0 && newCollection) {
        await Promise.all(
          initialItemIds.map(itemId =>
            addItemToCollection.mutateAsync({
              collectionId: newCollection.id,
              savedItemId: itemId,
            }).catch(error => {
              // Ignore duplicate errors
              if (error?.code !== '23505') throw error;
            })
          )
        );
        
        toast.success(
          `"${name}" created with ${initialItemIds.length} item${initialItemIds.length > 1 ? 's' : ''}`
        );
      } else {
        toast.success(`"${name}" is ready to organize your saves`);
      }

      // Notify parent
      if (onCreated && newCollection) {
        onCreated(newCollection.id);
      }

      // Reset form
      handleRemoveCover();
      setName('');
      setDescription('');
      setSelectedColor('primary');
      setIsCollaborative(false);
      setIsPublic(false);
      onOpenChange(false);
    } catch (error) {
      console.error('Create collection error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create collection');
    } finally {
      setIsCreating(false);
    }
  };

  // Show crop editor if file is selected
  if (cropFile) {
    return (
      <ImageCropEditor
        imageFile={cropFile}
        onSave={handleCropSave}
        onCancel={handleCropCancel}
        aspectRatio="16/9"
        hint="This will be your collection cover image"
      />
    );
  }

  return (
    <>
      <AnimatePresence>
        {open && (
          <DraggableBottomSheet
            open={open}
            onOpenChange={onOpenChange}
            title={
              <span className="flex items-center gap-2">
                <Folder className={cn("w-5 h-5", colorOptions.find(c => c.name === selectedColor)?.textClass || 'text-primary')} />
                New Collection
              </span>
            }
            height="auto"
          >
            <div className="space-y-6 px-5 pb-8 overflow-y-auto h-full">
              {/* Name */}
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Collection Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Summer WNTD List, Gift Ideas..."
                  className="w-full bg-surface-2 border border-border rounded-xl px-4 py-3.5 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  maxLength={50}
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
                />
              </div>

              {/* Color */}
              <div>
                <label className="text-sm font-medium text-foreground mb-3 block">
                  Color
                </label>
                <div className="flex gap-3">
                  {colorOptions.map((color) => (
                    <button
                      key={color.name}
                      onClick={() => setSelectedColor(color.name)}
                      className={cn(
                        "flex-grow aspect-square max-w-12 rounded-full transition-all",
                        color.bgClass,
                        selectedColor === color.name
                          ? "ring-2 ring-offset-2 ring-foreground scale-110"
                          : "hover:scale-105"
                      )}
                    />
                  ))}
                </div>
              </div>

              {/* More Options Accordion */}
              <Collapsible open={moreOptionsOpen} onOpenChange={setMoreOptionsOpen}>
                <CollapsibleTrigger className={cn(
                  "w-full flex items-center justify-between p-4 rounded-xl transition-colors group",
                  moreOptionsOpen 
                    ? "bg-orange-500/10" 
                    : "hover:bg-muted/50"
                )}>
                  <span className="font-medium text-orange-600 dark:text-orange-400">More options</span>
                  <motion.div
                    animate={{ rotate: moreOptionsOpen ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ChevronDown className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                  </motion.div>
                </CollapsibleTrigger>
                <CollapsibleContent className="overflow-hidden data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up">
                  <div className="space-y-6 pt-4">
                    {/* Cover Image */}
                    <div>
                      <label className="text-sm font-medium text-foreground mb-3 block">
                        Cover Image <span className="text-muted-foreground">(optional)</span>
                      </label>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                      {coverImageUrl ? (
                        <div className="relative aspect-[16/9] rounded-2xl overflow-hidden bg-surface-2">
                          <img
                            src={coverImageUrl}
                            alt="Collection cover"
                            className="w-full h-full object-cover"
                          />
                          <button
                            onClick={handleRemoveCover}
                            className="absolute top-2 right-2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                          >
                            ✕
                          </button>
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            className="absolute bottom-2 right-2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                          >
                            <Camera className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full aspect-[16/9] rounded-2xl border-2 border-dashed border-border bg-surface-2 hover:bg-surface-3 transition-colors flex flex-col items-center justify-center gap-2"
                        >
                          <ImageIcon className="w-8 h-8 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">Add cover image</span>
                        </button>
                      )}
                    </div>

                    {/* Description */}
                    <div>
                      <label className="text-sm font-medium text-foreground mb-2 block">
                        Description <span className="text-muted-foreground">(optional)</span>
                      </label>
                      <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="What's this collection for?"
                        className="w-full h-20 bg-surface-2 border border-border rounded-xl px-4 py-3 text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
                         maxLength={200}
                         onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); e.currentTarget.blur(); } }}
                       />
                    </div>

                    {/* Group Toggle */}
                    <div className="flex items-center justify-between p-4 bg-surface-2 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center">
                          <Users className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">Group</p>
                          <p className="text-sm text-muted-foreground">
                            Anyone with the link can add items
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={isCollaborative}
                        onCheckedChange={setIsCollaborative}
                      />
                    </div>

                    {/* Public Toggle */}
                    <div className="flex items-center justify-between p-4 bg-surface-2 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-accent/15 flex items-center justify-center">
                          <Globe className="w-5 h-5 text-accent-foreground" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">Public</p>
                          <p className="text-sm text-muted-foreground">
                            Share your vibe with the world
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={isPublic}
                        onCheckedChange={setIsPublic}
                      />
                    </div>

                    {/* Preview */}
                    <div className="p-4 bg-surface-2 rounded-2xl">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
                        Preview
                      </p>
                      <div className="flex items-center gap-3">
                        {coverImageUrl ? (
                          <div className="w-12 h-12 rounded-xl overflow-hidden">
                            <img src={coverImageUrl} alt="" className="w-full h-full object-cover" />
                          </div>
                        ) : (
                          <div 
                            className={cn(
                              "w-12 h-12 rounded-xl flex items-center justify-center",
                              colorOptions.find(c => c.name === selectedColor)?.bgClass || 'bg-primary'
                            )}
                          >
                            <Folder className="w-6 h-6 text-white" />
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-foreground">
                            {name || 'Collection Name'}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            0 items
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>

              {/* Create Button */}
              <button
                onClick={handleCreate}
                disabled={!name.trim() || isCreating}
                className={cn(
                  "w-full py-4 rounded-2xl font-medium text-base transition-all flex items-center justify-center gap-2",
                  name.trim() && !isCreating
                    ? "bg-primary text-primary-foreground hover:bg-primary/90"
                    : "bg-muted text-muted-foreground"
                )}
              >
                {isCreating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Create Collection
                  </>
                )}
              </button>

            </div>
          </DraggableBottomSheet>
        )}
      </AnimatePresence>
    </>
  );
}
